import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import axios from 'axios';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AccountTreeRounded,
  AddRounded,
  ApartmentRounded,
  ArchiveRounded,
  ChevronRightRounded,
  ExpandMoreRounded,
  HistoryRounded,
  PersonRounded,
  RefreshRounded,
  SearchRounded,
  SupervisorAccountRounded,
  WarningAmberRounded,
} from '@mui/icons-material';

import {
  archiveStacOrganizationEditorUnit,
  createStacOrganizationEditorUnit,
  getStacOrganizationEditor,
  moveStacOrganizationEditorUnit,
  placeStacOrganizationEditorWorker,
  renameStacOrganizationEditorUnit,
  updateStacOrganizationEditorManager,
} from '../services/stacService';
import {
  invalidateStacOrganizationQueries,
} from '../querySync';
import type {
  StacOrgEditorUnit,
  StacOrgEditorWorker,
} from '../types/stac';
import StacOrganizationHistoryDialog
  from './StacOrganizationHistoryDialog';
import StacOrganizationMapDialog
  from './StacOrganizationMapDialog';
import StacOrganizationMapWorkspace
  from './StacOrganizationMapWorkspace';
import StacOrganizationTreeWorkspace
  from './StacOrganizationTreeWorkspace';

type Selection =
  | { kind: 'unit'; id: string }
  | { kind: 'worker'; id: string };

function apiError(error: unknown, fallback: string): string {
  if (!axios.isAxiosError(error)) return fallback;
  const detail = error.response?.data?.detail;
  if (typeof detail !== 'string' || !detail.trim()) return fallback;

  const value = detail.toLowerCase();

  if (value.includes('would create a cycle')) {
    return 'Ese movimiento crearía un ciclo en el organigrama.';
  }
  if (value.includes('another active stac unit')) {
    return 'Ya existe una unidad con ese nombre dentro de la misma unidad superior.';
  }
  if (value.includes('must be empty before')) {
    return 'La unidad debe quedar vacía antes de archivarla.';
  }
  if (value.includes('must be located directly')) {
    return 'El responsable debe pertenecer directamente a esa unidad.';
  }
  if (value.includes('current manager of the existing unit')) {
    return 'Antes de mover a esta persona debes cambiar o retirar su responsabilidad actual.';
  }
  if (value.includes('still has organizational children')) {
    return 'Esta persona todavía tiene unidades heredadas del modelo anterior. Normaliza primero esas unidades.';
  }

  return detail;
}

function compareUnits(a: StacOrgEditorUnit, b: StacOrgEditorUnit) {
  if (a.is_root !== b.is_root) return a.is_root ? -1 : 1;
  return a.name.localeCompare(b.name, 'es');
}

function compareWorkers(a: StacOrgEditorWorker, b: StacOrgEditorWorker) {
  return a.display_name.localeCompare(b.display_name, 'es');
}

function descendantsOf(
  unitId: string,
  childrenByParent: Map<string, StacOrgEditorUnit[]>,
): Set<string> {
  const result = new Set<string>();

  const visit = (id: string) => {
    for (const child of childrenByParent.get(id) ?? []) {
      if (result.has(child.id)) continue;
      result.add(child.id);
      visit(child.id);
    }
  };

  visit(unitId);
  return result;
}

export default function StacOrganizationEditorView() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [search, setSearch] = useState('');
  const [selection, setSelection] = useState<Selection | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createParentId, setCreateParentId] = useState('');

  const [
    archiveTarget,
    setArchiveTarget,
  ] = useState<StacOrgEditorUnit | null>(
    null
  );

  const [
    historySelection,
    setHistorySelection,
  ] = useState<Selection | null>(
    null
  );

  const [
    mapOpen,
    setMapOpen,
  ] = useState(false);

  const [nameDraft, setNameDraft] = useState('');
  const [parentDraft, setParentDraft] = useState('');
  const [managerDraft, setManagerDraft] = useState('');
  const [workerUnitDraft, setWorkerUnitDraft] = useState('');

  const editorQuery = useQuery({
    queryKey: ['stac-organization-editor'],
    queryFn: getStacOrganizationEditor,
    retry: false,
    staleTime: 0,
    refetchOnWindowFocus: 'always',
  });

  const units = editorQuery.data?.units ?? [];
  const workers = editorQuery.data?.workers ?? [];

  const rootUnit = useMemo(
    () => units.find((unit) => unit.is_root) ?? null,
    [units],
  );

  const unitById = useMemo(
    () => new Map(units.map((unit) => [unit.id, unit])),
    [units],
  );

  const workerById = useMemo(
    () => new Map(workers.map((worker) => [worker.worker_id, worker])),
    [workers],
  );

  const validUnits = useMemo(
    () => units.filter((unit) => !unit.has_legacy_parent).sort(compareUnits),
    [units],
  );

  const legacyUnits = useMemo(
    () => units.filter((unit) => unit.has_legacy_parent).sort(compareUnits),
    [units],
  );

  const childrenByParent = useMemo(() => {
    const map = new Map<string, StacOrgEditorUnit[]>();

    for (const unit of validUnits) {
      if (!unit.parent_unit_id) continue;
      const children = map.get(unit.parent_unit_id) ?? [];
      children.push(unit);
      map.set(unit.parent_unit_id, children);
    }

    for (const children of map.values()) children.sort(compareUnits);
    return map;
  }, [validUnits]);

  const selectedUnit =
    selection?.kind === 'unit' ? unitById.get(selection.id) ?? null : null;

  const selectedWorker =
    selection?.kind === 'worker'
      ? workerById.get(
        selection.id
      )
        ?? null
      : null;

  const selectedUnitEffectiveManager =
    useMemo(() => {
      if (
        !selectedUnit
        || selectedUnit.is_root
      ) {
        return null;
      }

      const visited =
        new Set<string>();

      let current:
        StacOrgEditorUnit | null =
          selectedUnit;

      while (
        current
        && !visited.has(
          current.id
        )
      ) {
        visited.add(
          current.id
        );

        if (
          current
            .manager_worker_id
          && current
            .manager_display_name
        ) {
          return {
            workerId:
              current
                .manager_worker_id,

            displayName:
              current
                .manager_display_name,

            sourceUnitId:
              current.id,

            sourceUnitName:
              current.name,

            inherited:
              current.id
              !== selectedUnit.id,
          };
        }

        current =
          current.parent_unit_id
            ? unitById.get(
              current.parent_unit_id
            )
              ?? null
            : null;
      }

      return null;
    }, [
      selectedUnit,
      unitById,
    ]);

  useEffect(() => {
    if (!rootUnit || selection) return;
    setSelection({ kind: 'unit', id: rootUnit.id });
    setExpanded(new Set([rootUnit.id]));
  }, [rootUnit, selection]);

  useEffect(() => {
    if (!selectedUnit) return;
    setNameDraft(selectedUnit.name);
    setParentDraft(selectedUnit.parent_unit_id ?? '');
    setManagerDraft(selectedUnit.manager_worker_id ?? '');
  }, [selectedUnit]);

  useEffect(() => {
    if (!selectedWorker) return;
    setWorkerUnitDraft(selectedWorker.unit_id ?? '');
  }, [selectedWorker]);

  const workersByUnit = useMemo(() => {
    const map =
      new Map<
        string,
        StacOrgEditorWorker[]
      >();

    for (const worker of workers) {
      if (
        !worker.unit_id
        || worker.is_unassigned
        || worker.has_legacy_placement
      ) {
        continue;
      }

      const current =
        map.get(worker.unit_id)
        ?? [];

      current.push(worker);

      map.set(
        worker.unit_id,
        current,
      );
    }

    for (const unitWorkers of map.values()) {
      unitWorkers.sort(
        compareWorkers
      );
    }

    return map;
  }, [workers]);

  const normalizedSearch =
    search
      .trim()
      .toLocaleLowerCase('es');

  const trayWorkers =
    useMemo(() => {
      const source =
        normalizedSearch
          ? workers
          : workers.filter(
            (worker) =>
              worker.is_unassigned
              || worker
                .has_legacy_placement
          );

      return [...source]
        .sort(compareWorkers)
        .filter(
          (worker) =>
            !normalizedSearch
            || worker
              .display_name
              .toLocaleLowerCase('es')
              .includes(
                normalizedSearch
              ),
        );
    }, [
      workers,
      normalizedSearch,
    ]);


  const normalizedUnitOptions = useMemo(
    () => validUnits.filter((unit) => !unit.is_root),
    [validUnits],
  );

  const unitMoveOptions = useMemo(() => {
    if (!selectedUnit) return [];
    const descendants = descendantsOf(selectedUnit.id, childrenByParent);

    return validUnits.filter(
      (unit) => unit.id !== selectedUnit.id && !descendants.has(unit.id),
    );
  }, [selectedUnit, childrenByParent, validUnits]);

  const managerOptions = useMemo(() => {
    if (!selectedUnit) return [];
    return workers
      .filter(
        (worker) =>
          worker.unit_id === selectedUnit.id && !worker.has_legacy_placement,
      )
      .sort(compareWorkers);
  }, [selectedUnit, workers]);

  const structureUnitCount =
    validUnits.filter(
      (unit) => !unit.is_root
    ).length;

  const unassignedWorkerCount =
    workers.filter(
      (worker) =>
        worker.is_unassigned
        && !worker.has_legacy_placement
    ).length;

  const legacyWorkerCount =
    workers.filter(
      (worker) =>
        worker.has_legacy_placement
    ).length;

  const legacyUnitCount =
    legacyUnits.length;

  const legacyRelationshipCount =
    editorQuery.data
      ?.legacy_relationship_count
    ?? 0;

  const invalidate = async () => {
    await invalidateStacOrganizationQueries(
      queryClient
    );
  };

  const createMutation = useMutation({
    mutationFn: createStacOrganizationEditorUnit,
    onSuccess: async (result) => {
      setCreateOpen(false);
      setCreateName('');
      if (result.unit_node_id) {
        setSelection({ kind: 'unit', id: result.unit_node_id });
      }
      enqueueSnackbar('Unidad creada.', { variant: 'success' });
      await invalidate();
    },
    onError: (error) =>
      enqueueSnackbar(apiError(error, 'No se pudo crear la unidad.'), {
        variant: 'error',
      }),
  });

  const renameMutation = useMutation({
    mutationFn: ({ unitId, name }: { unitId: string; name: string }) =>
      renameStacOrganizationEditorUnit(unitId, { name }),
    onSuccess: async () => {
      enqueueSnackbar('Nombre actualizado.', { variant: 'success' });
      await invalidate();
    },
    onError: (error) =>
      enqueueSnackbar(apiError(error, 'No se pudo renombrar la unidad.'), {
        variant: 'error',
      }),
  });

  const moveMutation = useMutation({
    mutationFn: ({
      unitId,
      parentUnitId,
    }: {
      unitId: string;
      parentUnitId: string;
    }) =>
      moveStacOrganizationEditorUnit(unitId, {
        parent_unit_id: parentUnitId,
        effective_at: null,
      }),
    onSuccess: async () => {
      enqueueSnackbar('Unidad reubicada.', { variant: 'success' });
      await invalidate();
    },
    onError: (error) =>
      enqueueSnackbar(apiError(error, 'No se pudo reubicar la unidad.'), {
        variant: 'error',
      }),
  });

  const placementMutation = useMutation({
    mutationFn: ({
      workerId,
      unitId,
    }: {
      workerId: string;
      unitId: string | null;
    }) =>
      placeStacOrganizationEditorWorker(workerId, {
        unit_id: unitId,
        effective_at: null,
      }),
    onSuccess: async () => {
      enqueueSnackbar('Ubicación actualizada.', { variant: 'success' });
      await invalidate();
    },
    onError: (error) =>
      enqueueSnackbar(apiError(error, 'No se pudo actualizar la ubicación.'), {
        variant: 'error',
      }),
  });

  const managerMutation = useMutation({
    mutationFn: ({
      unitId,
      managerWorkerId,
    }: {
      unitId: string;
      managerWorkerId: string | null;
    }) =>
      updateStacOrganizationEditorManager(unitId, {
        manager_worker_id: managerWorkerId,
      }),
    onSuccess: async () => {
      enqueueSnackbar('Responsable actualizado.', { variant: 'success' });
      await invalidate();
    },
    onError: (error) =>
      enqueueSnackbar(apiError(error, 'No se pudo actualizar el responsable.'), {
        variant: 'error',
      }),
  });

  const archiveMutation = useMutation({
    mutationFn: archiveStacOrganizationEditorUnit,

    onSuccess: async () => {
      setArchiveTarget(null);

      if (rootUnit) {
        setSelection({
          kind: 'unit',
          id: rootUnit.id,
        });
      }

      enqueueSnackbar(
        'Unidad archivada.',
        {
          variant: 'success',
        },
      );

      await invalidate();
    },
    onError: (error) =>
      enqueueSnackbar(apiError(error, 'No se pudo archivar la unidad.'), {
        variant: 'error',
      }),
  });

  const toggle = (unitId: string) => {
    setExpanded((current) => {
      const next = new Set(current);

      if (next.has(unitId)) {
        next.delete(unitId);
      } else {
        next.add(unitId);
      }

      return next;
    });
  };

  const expandPathToUnit = (
    unitId: string,
  ) => {
    setExpanded((current) => {
      const next =
        new Set(current);

      const visited =
        new Set<string>();

      let currentUnitId:
        string | null = unitId;

      while (
        currentUnitId
        && !visited.has(
          currentUnitId
        )
      ) {
        visited.add(
          currentUnitId
        );

        next.add(
          currentUnitId
        );

        currentUnitId =
          unitById.get(
            currentUnitId
          )?.parent_unit_id
          ?? null;
      }

      if (rootUnit) {
        next.add(
          rootUnit.id
        );
      }

      return next;
    });
  };

  const selectWorkerAndLocate = (
    worker: StacOrgEditorWorker,
  ) => {
    setSelection({
      kind: 'worker',
      id: worker.worker_id,
    });

    if (!worker.unit_id) {
      return;
    }

    expandPathToUnit(
      worker.unit_id
    );

    window.setTimeout(
      () => {
        document
          .getElementById(
            `stac-org-worker-${worker.worker_id}`
          )
          ?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          });
      },
      50,
    );
  };

  const openCreate = (parentId?: string) => {
    setCreateParentId(parentId ?? rootUnit?.id ?? '');
    setCreateName('');
    setCreateOpen(true);
  };

  const renderUnit = (
    unit: StacOrgEditorUnit,
    depth = 0,
  ): ReactNode => {
    const children =
      childrenByParent.get(
        unit.id
      )
      ?? [];

    const directWorkers =
      workersByUnit.get(
        unit.id
      )
      ?? [];

    const hasContent =
      children.length > 0
      || directWorkers.length > 0;

    const isOpen =
      expanded.has(
        unit.id
      );

    const selected =
      selection?.kind === 'unit'
      && selection.id === unit.id;

    return (
      <Box
        key={unit.id}
        sx={{
          ml: depth
            ? 1.6
            : 0,
        }}
      >
        <Paper
          elevation={0}
          onClick={() =>
            setSelection({
              kind: 'unit',
              id: unit.id,
            })
          }
          sx={{
            cursor: 'pointer',
            borderRadius: 2.5,
            border:
              selected
                ? (
                  '1px solid '
                  + 'rgba(56,189,248,0.65)'
                )
                : (
                  '1px solid '
                  + 'rgba(148,163,184,0.13)'
                ),
            bgcolor:
              selected
                ? 'rgba(14,165,233,0.08)'
                : 'rgba(2,6,23,0.28)',
            px: 1.1,
            py: 1,
          }}
        >
          <Stack
            direction="row"
            spacing={0.8}
            alignItems="center"
          >
            <IconButton
              size="small"
              disabled={!hasContent}
              onClick={(event) => {
                event.stopPropagation();

                toggle(
                  unit.id
                );
              }}
              sx={{
                width: 28,
                height: 28,
              }}
            >
              {hasContent ? (
                isOpen ? (
                  <ExpandMoreRounded />
                ) : (
                  <ChevronRightRounded />
                )
              ) : null}
            </IconButton>

            <ApartmentRounded
              sx={{
                color: '#38bdf8',
                fontSize: 19,
              }}
            />

            <Box
              sx={{
                minWidth: 0,
                flex: 1,
              }}
            >
              <Typography
                noWrap
                sx={{
                  color: '#f8fafc',
                  fontWeight: 900,
                }}
              >
                {unit.name}
              </Typography>

              <Stack
                direction="row"
                spacing={0.6}
                useFlexGap
                flexWrap="wrap"
              >
                {unit
                  .manager_display_name ? (
                  <Typography
                    sx={{
                      color: '#7dd3fc',
                      fontSize: '0.72rem',
                    }}
                  >
                    Resp.
                    {' '}
                    {
                      unit
                        .manager_display_name
                    }
                  </Typography>
                ) : null}

                {!unit.is_root ? (
                  <Typography
                    sx={{
                      color: '#64748b',
                      fontSize: '0.72rem',
                    }}
                  >
                    {
                      unit
                        .direct_worker_count
                    }
                    {' '}
                    pers. ·
                    {' '}
                    {
                      unit
                        .child_unit_count
                    }
                    {' '}
                    sub.
                  </Typography>
                ) : (
                  <Typography
                    sx={{
                      color: '#64748b',
                      fontSize: '0.72rem',
                    }}
                  >
                    Raíz organizativa
                  </Typography>
                )}
              </Stack>
            </Box>
          </Stack>
        </Paper>

        {isOpen && hasContent ? (
          <Stack
            spacing={0.7}
            sx={{
              mt: 0.7,
              pl: 1.2,
              borderLeft:
                '1px solid rgba(148,163,184,0.14)',
            }}
          >
            {directWorkers.map(
              (worker) => {
                const workerSelected =
                  selection?.kind
                    === 'worker'
                  && selection.id
                    === worker.worker_id;

                const isManager =
                  unit.manager_worker_id
                    === worker.worker_id;

                return (
                  <Button
                    id={
                      `stac-org-worker-${worker.worker_id}`
                    }
                    key={
                      worker.worker_id
                    }
                    variant="text"
                    onClick={() =>
                      setSelection({
                        kind: 'worker',
                        id:
                          worker
                            .worker_id,
                      })
                    }
                    sx={{
                      width: '100%',
                      justifyContent:
                        'flex-start',
                      textTransform: 'none',
                      borderRadius: 2.2,
                      border:
                        workerSelected
                          ? (
                            '1px solid '
                            + 'rgba(56,189,248,0.42)'
                          )
                          : (
                            '1px solid '
                            + 'rgba(148,163,184,0.08)'
                          ),
                      bgcolor:
                        workerSelected
                          ? 'rgba(56,189,248,0.08)'
                          : 'rgba(15,23,42,0.34)',
                      px: 1.2,
                      py: 0.8,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{
                        width: '100%',
                        minWidth: 0,
                      }}
                    >
                      <PersonRounded
                        sx={{
                          color:
                            isManager
                              ? '#7dd3fc'
                              : '#94a3b8',
                          fontSize: 18,
                        }}
                      />

                      <Typography
                        noWrap
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          textAlign: 'left',
                          color: '#f8fafc',
                          fontWeight:
                            isManager
                              ? 900
                              : 750,
                          fontSize:
                            '0.82rem',
                        }}
                      >
                        {
                          worker
                            .display_name
                        }
                      </Typography>

                      {isManager ? (
                        <Chip
                          size="small"
                          label="Responsable"
                          variant="outlined"
                          sx={{
                            color: '#7dd3fc',
                          }}
                        />
                      ) : null}
                    </Stack>
                  </Button>
                );
              },
            )}

            {children.map(
              (child) =>
                renderUnit(
                  child,
                  depth + 1,
                ),
            )}
          </Stack>
        ) : null}
      </Box>
    );
  };


  if (editorQuery.isLoading) {
    return (
      <Paper elevation={0} sx={{ p: 2.5, bgcolor: 'rgba(15,23,42,0.72)' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={20} />
          <Typography sx={{ color: '#94a3b8' }}>
            Cargando editor organizativo...
          </Typography>
        </Stack>
      </Paper>
    );
  }

  if (editorQuery.isError || !editorQuery.data) {
    return (
      <Alert
        severity="error"
        variant="outlined"
        action={
          <Button color="inherit" onClick={() => void editorQuery.refetch()}>
            Reintentar
          </Button>
        }
      >
        No se pudo cargar el editor organizativo STAC.
      </Alert>
    );
  }

  return (
    <>
      <Stack spacing={2}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: '1px solid rgba(148,163,184,0.16)',
            bgcolor: 'rgba(15,23,42,0.72)',
            p: 2.3,
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', md: 'center' }}
            spacing={1.5}
          >
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <AccountTreeRounded sx={{ color: '#38bdf8' }} />
                <Typography sx={{ color: '#f8fafc', fontWeight: 950 }}>
                  Organización
                </Typography>
              </Stack>
              <Typography sx={{ mt: 0.4, color: '#94a3b8', fontSize: '0.86rem' }}>
                Diseña la estructura, ubica personas y establece responsables.
              </Typography>
            </Box>

            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
            >
              <Button
                variant="outlined"
                startIcon={
                  <AccountTreeRounded />
                }
                onClick={() => {
                  setExpanded(
                    new Set(
                      validUnits.map(
                        (unit) =>
                          unit.id
                      )
                    )
                  );

                  setMapOpen(true);
                }}
                sx={{
                  textTransform: 'none',
                  fontWeight: 800,
                }}
              >
                Ver mapa organizativo
              </Button>

              <Button
                variant="outlined"
                startIcon={<RefreshRounded />}
                onClick={() => void editorQuery.refetch()}
                sx={{ textTransform: 'none', fontWeight: 800 }}
              >
                Actualizar
              </Button>

              <Button
                variant="contained"
                startIcon={<AddRounded />}
                onClick={() =>
                  openCreate(
                    selectedUnit && !selectedUnit.has_legacy_parent
                      ? selectedUnit.id
                      : rootUnit?.id,
                  )
                }
                sx={{ textTransform: 'none', fontWeight: 900 }}
              >
                Nueva unidad
              </Button>
            </Stack>
          </Stack>

          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            sx={{
              mt: 1.5,
            }}
          >
            <Chip
              size="small"
              label={`${structureUnitCount} ${
                structureUnitCount === 1
                  ? 'unidad en estructura'
                  : 'unidades en estructura'
              }`}
            />

            <Chip
              size="small"
              label={`${workers.length} ${
                workers.length === 1
                  ? 'persona'
                  : 'personas'
              }`}
            />

            <Chip
              size="small"
              label={`${unassignedWorkerCount} ${
                unassignedWorkerCount === 1
                  ? 'persona sin ubicar'
                  : 'personas sin ubicar'
              }`}
              sx={{
                color:
                  unassignedWorkerCount
                    ? '#fbbf24'
                    : undefined,
              }}
            />

            {legacyRelationshipCount > 0 ? (
              <Chip
                size="small"
                label={`${legacyRelationshipCount} ${
                  legacyRelationshipCount === 1
                    ? 'relación por normalizar'
                    : 'relaciones por normalizar'
                }`}
                sx={{
                  color: '#fbbf24',
                }}
              />
            ) : null}
          </Stack>
        </Paper>

        {legacyRelationshipCount > 0 ? (
          <Alert
            severity="warning"
            variant="outlined"
            icon={
              <WarningAmberRounded />
            }
          >
            Hay {legacyRelationshipCount}
            {' '}
            {legacyRelationshipCount === 1
              ? 'relación heredada'
              : 'relaciones heredadas'}
            {' '}
            del modelo anterior.
            {' '}
            {legacyUnitCount > 0
              ? `${legacyUnitCount} corresponden a unidades. `
              : ''}
            {legacyWorkerCount > 0
              ? `${legacyWorkerCount} corresponden a personas. `
              : ''}
            Si una unidad es válida, asígnale una unidad superior
            para normalizarla. Si fue creada solo durante las pruebas
            y está vacía, archívala.
          </Alert>
        ) : null}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              lg:
                'minmax(220px,.55fr) '
                + 'minmax(390px,1.25fr) '
                + 'minmax(300px,.8fr)',
            },
            gap: 1.5,
            alignItems: 'start',
          }}
        >
          <Box
            sx={{
              gridColumn: {
                xs: '1',
                lg: '1 / span 2',
              },
            }}
          >
            {rootUnit ? (
              <StacOrganizationTreeWorkspace
                rootUnit={rootUnit}
                legacyUnits={
                  legacyUnits
                }
                unitById={
                  unitById
                }
                workerById={
                  workerById
                }
                childrenByParent={
                  childrenByParent
                }
                workersByUnit={
                  workersByUnit
                }
                expanded={
                  expanded
                }
                selection={
                  selection
                }
                search={
                  search
                }
                trayWorkers={
                  trayWorkers
                }
                unassignedWorkerCount={
                  unassignedWorkerCount
                }
                busy={
                  placementMutation
                    .isPending
                  || moveMutation
                    .isPending
                }
                onSearchChange={
                  setSearch
                }
                onLocateWorker={
                  selectWorkerAndLocate
                }
                onSelect={
                  setSelection
                }
                onToggle={
                  toggle
                }
                onExpandAll={() =>
                  setExpanded(
                    new Set(
                      validUnits.map(
                        (unit) =>
                          unit.id
                      )
                    )
                  )
                }
                onCollapseAll={() =>
                  setExpanded(
                    rootUnit
                      ? new Set([
                        rootUnit.id,
                      ])
                      : new Set()
                  )
                }
                onMoveWorker={(
                  workerId,
                  targetUnitId,
                ) => {
                  if (
                    targetUnitId
                  ) {
                    expandPathToUnit(
                      targetUnitId
                    );
                  }

                  placementMutation.mutate({
                    workerId,
                    unitId:
                      targetUnitId,
                  });
                }}
                onMoveUnit={(
                  unitId,
                  targetUnitId,
                ) => {
                  setExpanded(
                    (current) => {
                      const next =
                        new Set(
                          current
                        );

                      next.add(
                        targetUnitId
                      );

                      return next;
                    }
                  );

                  moveMutation.mutate({
                    unitId,
                    parentUnitId:
                      targetUnitId,
                  });
                }}
              />
            ) : (
              <Alert
                severity="error"
                variant="outlined"
              >
                No existe una raíz
                organizativa válida.
              </Alert>
            )}
          </Box>

          <Paper
            elevation={0}
            sx={{
              ...panelSx,
              display: 'none',
            }}
          >
            <Typography
              sx={panelTitleSx}
            >
              Personas
            </Typography>

            <Typography
              sx={panelHelpSx}
            >
              Personas sin ubicar.
              Busca por nombre para localizar
              cualquier persona en el organigrama.
            </Typography>

            <TextField
              size="small"
              placeholder="Buscar persona"
              value={search}
              onChange={(event) =>
                setSearch(
                  event.target.value
                )
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment
                    position="start"
                  >
                    <SearchRounded
                      sx={{
                        fontSize: 18,
                        color: '#64748b',
                      }}
                    />
                  </InputAdornment>
                ),
              }}
              fullWidth
              sx={{
                mt: 1.2,
              }}
            />

            <Stack
              direction="row"
              spacing={0.7}
              sx={{
                mt: 1,
              }}
            >
              <Chip
                size="small"
                label={
                  normalizedSearch
                    ? (
                      `${trayWorkers.length} ${
                        trayWorkers.length
                          === 1
                          ? 'resultado'
                          : 'resultados'
                      }`
                    )
                    : (
                      `${unassignedWorkerCount} ${
                        unassignedWorkerCount
                          === 1
                          ? 'sin ubicar'
                          : 'sin ubicar'
                      }`
                    )
                }
                variant="outlined"
                sx={{
                  color:
                    !normalizedSearch
                    && unassignedWorkerCount
                      ? '#fbbf24'
                      : undefined,
                }}
              />
            </Stack>

            <Divider
              sx={{
                my: 1.2,
              }}
            />

            {trayWorkers.length ? (
              <Stack
                spacing={0.55}
                sx={{
                  maxHeight: 500,
                  overflowY: 'auto',
                }}
              >
                {trayWorkers.map(
                  (worker) => {
                    const selected =
                      selection?.kind
                        === 'worker'
                      && selection.id
                        === worker.worker_id;

                    const unitName =
                      worker.unit_id
                        ? unitById.get(
                          worker.unit_id
                        )?.name
                        : null;

                    return (
                      <Button
                        key={
                          worker.worker_id
                        }
                        variant="text"
                        onClick={() =>
                          selectWorkerAndLocate(
                            worker
                          )
                        }
                        sx={{
                          display: 'block',
                          textAlign: 'left',
                          textTransform:
                            'none',
                          borderRadius: 2,
                          px: 1,
                          py: 0.8,
                          border:
                            selected
                              ? (
                                '1px solid '
                                + 'rgba(56,189,248,0.30)'
                              )
                              : (
                                '1px solid '
                                + 'transparent'
                              ),
                          bgcolor:
                            selected
                              ? 'rgba(56,189,248,0.10)'
                              : 'transparent',
                        }}
                      >
                        <Typography
                          noWrap
                          sx={{
                            color: '#f8fafc',
                            fontWeight: 850,
                          }}
                        >
                          {
                            worker
                              .display_name
                          }
                        </Typography>

                        <Typography
                          sx={{
                            mt: 0.25,
                            color:
                              worker
                                .has_legacy_placement
                                ? '#fbbf24'
                                : worker
                                    .is_unassigned
                                  ? '#fbbf24'
                                  : '#64748b',
                            fontSize:
                              '0.72rem',
                          }}
                        >
                          {worker
                            .has_legacy_placement
                            ? 'Pendiente de normalizar'
                            : worker
                                .is_unassigned
                              ? 'Sin ubicar'
                              : (
                                unitName
                                ?? 'Ubicada'
                              )}
                        </Typography>
                      </Button>
                    );
                  },
                )}
              </Stack>
            ) : normalizedSearch ? (
              <Alert
                severity="info"
                variant="outlined"
              >
                No se encontraron personas
                con ese nombre.
              </Alert>
            ) : (
              <Alert
                severity="success"
                variant="outlined"
              >
                Todas las personas están
                ubicadas en el organigrama.
              </Alert>
            )}
          </Paper>

          <Paper
            elevation={0}
            sx={{
              ...panelSx,
              display: 'none',
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box>
                <Typography sx={panelTitleSx}>
                  Organigrama
                </Typography>
                <Typography sx={panelHelpSx}>
                  Unidades, responsables y personas
                  dentro de la estructura organizativa.
                </Typography>
              </Box>

              <Stack direction="row">
                <Tooltip title="Expandir todo">
                  <IconButton
                    size="small"
                    onClick={() => setExpanded(new Set(validUnits.map((u) => u.id)))}
                  >
                    <ExpandMoreRounded />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Contraer">
                  <IconButton
                    size="small"
                    onClick={() =>
                      setExpanded(rootUnit ? new Set([rootUnit.id]) : new Set())
                    }
                  >
                    <ChevronRightRounded />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>

            <Divider sx={{ my: 1.2 }} />

            {rootUnit ? renderUnit(rootUnit) : (
              <Alert severity="error" variant="outlined">
                No existe una raíz organizativa válida.
              </Alert>
            )}

            {legacyUnits.length ? (
              <Stack spacing={0.7} sx={{ mt: 2 }}>
                <Typography sx={{ color: '#fbbf24', fontWeight: 900, fontSize: '0.78rem' }}>
                  Pendiente de normalizar
                </Typography>

                {legacyUnits.map((unit) => (
                  <Paper
                    key={unit.id}
                    elevation={0}
                    onClick={() => setSelection({ kind: 'unit', id: unit.id })}
                    sx={{
                      cursor: 'pointer',
                      p: 1,
                      borderRadius: 2,
                      border: '1px solid rgba(245,158,11,0.25)',
                      bgcolor:
                        selection?.kind === 'unit' && selection.id === unit.id
                          ? 'rgba(245,158,11,0.10)'
                          : 'rgba(245,158,11,0.04)',
                    }}
                  >
                    <Typography sx={{ color: '#f8fafc', fontWeight: 850 }}>
                      {unit.name}
                    </Typography>
                    <Typography sx={{ color: '#94a3b8', fontSize: '0.72rem' }}>
                      Bajo {unit.legacy_parent_display_name ?? 'relación heredada'}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            ) : null}
          </Paper>

          <Paper elevation={0} sx={panelSx}>
            {selectedUnit ? (
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <ApartmentRounded sx={{ color: '#38bdf8' }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography noWrap sx={panelTitleSx}>{selectedUnit.name}</Typography>
                    <Typography sx={panelHelpSx}>
                      {selectedUnit.is_root ? 'Raíz organizativa' : 'Unidad organizativa'}
                    </Typography>
                  </Box>
                </Stack>

                <Stack
                  direction="row"
                  spacing={0.7}
                  useFlexGap
                  flexWrap="wrap"
                >
                  <Chip
                    size="small"
                    label={`${selectedUnit.direct_worker_count} personas`}
                  />

                  <Chip
                    size="small"
                    label={`${selectedUnit.child_unit_count} subunidades`}
                  />
                </Stack>

                <Button
                  variant="outlined"
                  size="small"
                  startIcon={
                    <HistoryRounded />
                  }
                  onClick={() =>
                    setHistorySelection({
                      kind: 'unit',
                      id: selectedUnit.id,
                    })
                  }
                  sx={{
                    alignSelf: 'flex-start',
                    textTransform: 'none',
                    fontWeight: 800,
                  }}
                >
                  Histórico
                </Button>

                {selectedUnit.has_legacy_parent ? (
                  <Alert
                    severity="warning"
                    variant="outlined"
                  >
                    Esta unidad procede del modelo anterior y estaba
                    situada bajo
                    {' '}
                    <strong>
                      {
                        selectedUnit
                          .legacy_parent_display_name
                        ?? 'una persona'
                      }
                    </strong>
                    .
                    {' '}
                    Si quieres conservarla, selecciona una unidad
                    superior válida y pulsa
                    {' '}
                    <strong>
                      Normalizar ubicación
                    </strong>
                    .
                    {' '}
                    Si fue creada únicamente durante las pruebas
                    y está vacía, puedes archivarla.
                  </Alert>
                ) : null}

                <Divider />

                {selectedUnit.is_root ? (
                  <Alert severity="info" variant="outlined">
                    La raíz contiene toda la estructura. Las personas ubicadas
                    directamente aquí se consideran sin ubicar. La regla de
                    responsable de la cúspide se definirá en una fase posterior.
                  </Alert>
                ) : (
                  <>
                    <Typography sx={fieldLabelSx}>NOMBRE</Typography>
                    <Stack direction="row" spacing={0.7}>
                      <TextField
                        size="small"
                        value={nameDraft}
                        onChange={(event) => setNameDraft(event.target.value)}
                        fullWidth
                      />
                      <Button
                        variant="outlined"
                        disabled={
                          renameMutation.isPending ||
                          !nameDraft.trim() ||
                          nameDraft.trim() === selectedUnit.name
                        }
                        onClick={() =>
                          renameMutation.mutate({
                            unitId: selectedUnit.id,
                            name: nameDraft.trim(),
                          })
                        }
                      >
                        Guardar
                      </Button>
                    </Stack>

                    <Typography sx={fieldLabelSx}>UNIDAD SUPERIOR</Typography>
                    <TextField
                      select
                      size="small"
                      value={parentDraft}
                      onChange={(event) => setParentDraft(event.target.value)}
                      fullWidth
                    >
                      {unitMoveOptions.map((unit) => (
                        <MenuItem key={unit.id} value={unit.id}>
                          {unit.name}{unit.is_root ? ' · Raíz' : ''}
                        </MenuItem>
                      ))}
                    </TextField>
                    <Button
                      variant="outlined"
                      disabled={
                        moveMutation.isPending ||
                        !parentDraft ||
                        parentDraft === selectedUnit.parent_unit_id
                      }
                      onClick={() =>
                        moveMutation.mutate({
                          unitId: selectedUnit.id,
                          parentUnitId: parentDraft,
                        })
                      }
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      {selectedUnit.has_legacy_parent
                        ? 'Normalizar ubicación'
                        : 'Mover unidad'}
                    </Button>

                    <Typography sx={fieldLabelSx}>
                      RESPONSABLE PROPIO
                    </Typography>
                    <TextField
                      select
                      size="small"
                      value={managerDraft}
                      onChange={(event) => setManagerDraft(event.target.value)}
                      helperText={
                        'Opcional. Solo personas ubicadas '
                        + 'directamente en esta unidad.'
                      }
                      fullWidth
                    >
                      <MenuItem value="">Sin responsable propio</MenuItem>
                      {managerOptions.map((worker) => (
                        <MenuItem key={worker.worker_id} value={worker.worker_id}>
                          {worker.display_name}
                        </MenuItem>
                      ))}
                    </TextField>
                    <Button
                      variant="outlined"
                      startIcon={
                        <SupervisorAccountRounded />
                      }
                      disabled={
                        managerMutation.isPending
                        || managerDraft
                          === (
                            selectedUnit
                              .manager_worker_id
                            ?? ''
                          )
                      }
                      onClick={() =>
                        managerMutation.mutate({
                          unitId:
                            selectedUnit.id,
                          managerWorkerId:
                            managerDraft
                            || null,
                        })
                      }
                      sx={{
                        alignSelf:
                          'flex-start',
                      }}
                    >
                      Guardar responsable
                    </Button>

                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.3,
                        borderRadius: 2.5,
                        border:
                          '1px solid '
                          + 'rgba(56,189,248,0.18)',
                        bgcolor:
                          'rgba(14,165,233,0.05)',
                      }}
                    >
                      <Typography
                        sx={fieldLabelSx}
                      >
                        RESPONSABLE EFECTIVO
                      </Typography>

                      <Typography
                        sx={{
                          mt: 0.4,
                          color: '#f8fafc',
                          fontWeight: 900,
                        }}
                      >
                        {
                          selectedUnitEffectiveManager
                            ?.displayName
                          ?? 'Sin responsable efectivo'
                        }
                      </Typography>

                      {selectedUnitEffectiveManager
                        ?.inherited ? (
                        <Typography
                          sx={{
                            mt: 0.3,
                            color: '#7dd3fc',
                            fontSize: '0.75rem',
                          }}
                        >
                          Heredado de
                          {' '}
                          {
                            selectedUnitEffectiveManager
                              .sourceUnitName
                          }
                        </Typography>
                      ) : selectedUnitEffectiveManager ? (
                        <Typography
                          sx={{
                            mt: 0.3,
                            color: '#64748b',
                            fontSize: '0.75rem',
                          }}
                        >
                          Definido directamente
                          en esta unidad.
                        </Typography>
                      ) : null}
                    </Paper>

                    <Divider />

                    <Button
                      variant="outlined"
                      startIcon={<AddRounded />}
                      disabled={selectedUnit.has_legacy_parent}
                      onClick={() => openCreate(selectedUnit.id)}
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      Crear subunidad
                    </Button>

                    <Button
                      color="error"
                      variant="outlined"
                      startIcon={
                        <ArchiveRounded />
                      }
                      disabled={
                        archiveMutation.isPending
                        || selectedUnit
                          .direct_worker_count > 0
                        || selectedUnit
                          .child_unit_count > 0
                      }
                      onClick={() =>
                        setArchiveTarget(
                          selectedUnit
                        )
                      }
                      sx={{
                        alignSelf:
                          'flex-start',
                      }}
                    >
                      {selectedUnit
                        .has_legacy_parent
                        ? 'Archivar unidad heredada'
                        : 'Archivar unidad'}
                    </Button>

                    {selectedUnit.direct_worker_count > 0 ||
                    selectedUnit.child_unit_count > 0 ? (
                      <Typography sx={panelHelpSx}>
                        Para archivarla, reubica antes todas sus personas y subunidades.
                      </Typography>
                    ) : null}
                  </>
                )}
              </Stack>
            ) : selectedWorker ? (
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <PersonRounded sx={{ color: '#38bdf8' }} />
                  <Box>
                    <Typography sx={panelTitleSx}>{selectedWorker.display_name}</Typography>
                    <Typography sx={panelHelpSx}>Persona STAC</Typography>
                  </Box>
                </Stack>

                {selectedWorker.has_legacy_placement ? (
                  <Alert severity="warning" variant="outlined">
                    Su posición procede del modelo anterior. Selecciona una
                    unidad válida para normalizarla.
                  </Alert>
                ) : null}

                <Divider />

                <Typography sx={fieldLabelSx}>UNIDAD PRINCIPAL</Typography>
                <TextField
                  select
                  size="small"
                  value={workerUnitDraft}
                  onChange={(event) => setWorkerUnitDraft(event.target.value)}
                  helperText="Deja vacío para mantener a la persona sin ubicar."
                  fullWidth
                >
                  <MenuItem value="">Sin ubicar</MenuItem>
                  {normalizedUnitOptions.map((unit) => (
                    <MenuItem key={unit.id} value={unit.id}>{unit.name}</MenuItem>
                  ))}
                </TextField>

                <Button
                  variant="contained"
                  disabled={
                    placementMutation.isPending ||
                    workerUnitDraft === (selectedWorker.unit_id ?? '')
                  }
                  onClick={() =>
                    placementMutation.mutate({
                      workerId: selectedWorker.worker_id,
                      unitId: workerUnitDraft || null,
                    })
                  }
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Guardar ubicación
                </Button>

                <Paper
                  elevation={0}
                  sx={{
                    p: 1.3,
                    borderRadius: 2.5,
                    border:
                      '1px solid rgba(56,189,248,0.18)',
                    bgcolor:
                      'rgba(14,165,233,0.05)',
                  }}
                >
                  <Typography
                    sx={fieldLabelSx}
                  >
                    RESPONSABLE PRINCIPAL ACTUAL
                  </Typography>

                  <Typography
                    sx={{
                      mt: 0.4,
                      color: '#f8fafc',
                      fontWeight: 900,
                    }}
                  >
                    {
                      selectedWorker
                        .manager_display_name
                      ?? 'Sin responsable principal'
                    }
                  </Typography>
                </Paper>

                <Button
                  variant="outlined"
                  size="small"
                  startIcon={
                    <HistoryRounded />
                  }
                  onClick={() =>
                    setHistorySelection({
                      kind: 'worker',
                      id:
                        selectedWorker
                          .worker_id,
                    })
                  }
                  sx={{
                    alignSelf: 'flex-start',
                    textTransform: 'none',
                    fontWeight: 800,
                  }}
                >
                  Histórico
                </Button>
              </Stack>
            ) : (
              <Typography sx={{ color: '#94a3b8' }}>
                Selecciona una unidad o persona.
              </Typography>
            )}
          </Paper>
        </Box>
      </Stack>

      <StacOrganizationMapDialog
        open={mapOpen}
        onClose={() =>
          setMapOpen(false)
        }
      >
        {rootUnit ? (
          <StacOrganizationMapWorkspace
            rootUnit={rootUnit}
            unitById={
              unitById
            }
            workerById={
              workerById
            }
            childrenByParent={
              childrenByParent
            }
            workersByUnit={
              workersByUnit
            }
            expanded={
              expanded
            }
            selection={
              selection
            }
            search={
              search
            }
            trayWorkers={
              trayWorkers
            }
            unassignedWorkerCount={
              unassignedWorkerCount
            }
            busy={
              placementMutation
                .isPending
              || moveMutation
                .isPending
            }
            onSearchChange={
              setSearch
            }
            onLocateWorker={
              selectWorkerAndLocate
            }
            onSelect={
              setSelection
            }
            onToggle={
              toggle
            }
            onExpandAll={() =>
              setExpanded(
                new Set(
                  validUnits.map(
                    (unit) =>
                      unit.id
                  )
                )
              )
            }
            onCollapseAll={() =>
              setExpanded(
                rootUnit
                  ? new Set([
                    rootUnit.id,
                  ])
                  : new Set()
              )
            }
            onMoveWorker={(
              workerId,
              targetUnitId,
            ) => {
              if (
                targetUnitId
              ) {
                expandPathToUnit(
                  targetUnitId
                );
              }

              placementMutation.mutate({
                workerId,
                unitId:
                  targetUnitId,
              });
            }}
            onMoveUnit={(
              unitId,
              targetUnitId,
            ) => {
              setExpanded(
                (current) => {
                  const next =
                    new Set(
                      current
                    );

                  next.add(
                    targetUnitId
                  );

                  return next;
                }
              );

              moveMutation.mutate({
                unitId,
                parentUnitId:
                  targetUnitId,
              });
            }}
          />
        ) : (
          <Alert
            severity="error"
            variant="outlined"
          >
            No existe una raíz
            organizativa válida.
          </Alert>
        )}
      </StacOrganizationMapDialog>

      <StacOrganizationHistoryDialog
        open={
          Boolean(historySelection)
        }
        onClose={() =>
          setHistorySelection(
            null
          )
        }
        unit={
          historySelection?.kind
            === 'unit'
            ? unitById.get(
              historySelection.id
            )
              ?? null
            : null
        }
        worker={
          historySelection?.kind
            === 'worker'
            ? workerById.get(
              historySelection.id
            )
              ?? null
            : null
        }
      />

      <Dialog
        open={createOpen}
        onClose={() => !createMutation.isPending && setCreateOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: '#0f172a',
            backgroundImage: 'none',
            color: '#f8fafc',
          },
        }}
      >
        <DialogTitle>Nueva unidad</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Nombre"
              value={createName}
              onChange={(event) => setCreateName(event.target.value)}
              inputProps={{ maxLength: 240 }}
              autoFocus
              fullWidth
            />
            <TextField
              select
              label="Unidad superior"
              value={createParentId}
              onChange={(event) => setCreateParentId(event.target.value)}
              fullWidth
            >
              {validUnits.map((unit) => (
                <MenuItem key={unit.id} value={unit.id}>
                  {unit.name}{unit.is_root ? ' · Raíz' : ''}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={
              createMutation.isPending ||
              !createName.trim() ||
              !createParentId
            }
            onClick={() =>
              createMutation.mutate({
                name: createName.trim(),
                parent_unit_id: createParentId,
              })
            }
          >
            Crear unidad
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={
          Boolean(archiveTarget)
        }
        onClose={() => {
          if (
            !archiveMutation
              .isPending
          ) {
            setArchiveTarget(
              null
            );
          }
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: '#0f172a',
            backgroundImage: 'none',
            color: '#f8fafc',
          },
        }}
      >
        <DialogTitle>
          Archivar unidad
        </DialogTitle>

        <DialogContent>
          <Stack
            spacing={2}
            sx={{
              pt: 1,
            }}
          >
            <Typography
              sx={{
                color: '#f8fafc',
                fontWeight: 900,
              }}
            >
              {
                archiveTarget
                  ?.name
              }
            </Typography>

            {archiveTarget
              ?.has_legacy_parent ? (
              <Alert
                severity="warning"
                variant="outlined"
              >
                Esta unidad procede de las
                pruebas del modelo anterior.
                Se retirará de la estructura
                activa, pero su histórico
                permanecerá conservado.
              </Alert>
            ) : (
              <Alert
                severity="info"
                variant="outlined"
              >
                La unidad dejará de estar
                activa, pero su histórico
                organizativo se conservará.
              </Alert>
            )}

            <Typography
              sx={{
                color: '#94a3b8',
                fontSize: '0.84rem',
              }}
            >
              Solo pueden archivarse
              unidades sin personas y sin
              subunidades.
            </Typography>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() =>
              setArchiveTarget(
                null
              )
            }
            disabled={
              archiveMutation
                .isPending
            }
          >
            Cancelar
          </Button>

          <Button
            color="error"
            variant="contained"
            startIcon={
              <ArchiveRounded />
            }
            disabled={
              archiveMutation
                .isPending
              || !archiveTarget
            }
            onClick={() => {
              if (!archiveTarget) {
                return;
              }

              archiveMutation.mutate(
                archiveTarget.id
              );
            }}
          >
            {archiveMutation
              .isPending
              ? 'Archivando...'
              : 'Archivar unidad'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

const panelSx = {
  borderRadius: 3,
  border: '1px solid rgba(148,163,184,0.14)',
  bgcolor: 'rgba(15,23,42,0.72)',
  p: 1.7,
  minHeight: { lg: 620 },
};

const panelTitleSx = {
  color: '#f8fafc',
  fontWeight: 900,
};

const panelHelpSx = {
  color: '#64748b',
  fontSize: '0.76rem',
};

const fieldLabelSx = {
  color: '#94a3b8',
  fontSize: '0.72rem',
  fontWeight: 900,
};
